import { useState } from "react";
import { Link, useParams } from "react-router";
import { api, ApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import type { MessageKey } from "../lib/messages";
import { useAsync } from "../lib/useAsync";
import { IconCall, IconChat, IconEye, IconHeart, IconRocket, IconShare, IconWhatsApp } from "../ui/icons";
import { AsyncButton, ConfirmDialog, ErrorState, Loading, MoneyInput, Notice, PageHeader, PriceGuideBar, QtyStepper, Switch, VerdictTag, useToast, type PriceGuide, type Verdict } from "../ui/kit";

type Insights = {
  listing: {
    id: string;
    title: string;
    status: string;
    priceCents: number | null;
    wasCents: number | null;
    negotiable: boolean;
    currency: string;
    condition: string;
    quantityAvailable: number;
    quantityTotal: number;
    publishedAt: string | null;
    expiresAt: string | null;
    coverUrl: string | null;
  };
  totals: { views: number; saves: number; chats: number; whatsapp: number; calls: number; shares: number; views7d: number; totalViews: number; leads: number };
  series: { day: string; views: number; leads: number }[];
  deals: { id: string; status: string; quantity: number; agreedPriceCents: number | null; buyerName: string; source: string; lastMessageAt: string; awaiting: "you" | "buyer" | null }[];
  market: { guide: PriceGuide | null; verdict: Verdict | null; rank: { position: number; of: number } | null };
  bump: { canBump: boolean; availableAt: string | null };
  tips: { code: string; value?: number }[];
};

/** One listing's numbers and every tool to sell it faster, all free. */
export function ManageListing() {
  const { id } = useParams();
  const { t, money, relative, date } = useI18n();
  const toast = useToast();
  const { data, error, loading, reload } = useAsync(() => api<Insights>(`/api/listings/${id}/insights`), [id]);
  const [price, setPrice] = useState<number | null>(null);
  const [soldOpen, setSoldOpen] = useState(false);
  const [soldQty, setSoldQty] = useState(1);
  const [err, setErr] = useState<string | null>(null);

  if (loading && !data) return <Loading />;
  if (error || !data) return <ErrorState error={error} onRetry={reload} />;
  const l = data.listing;
  const cur = l.currency;
  const live = ["active", "reserved", "sold_out"].includes(l.status);
  const maxBar = Math.max(1, ...data.series.map((s) => s.views));

  const run = async (fn: () => Promise<unknown>, done: string) => {
    setErr(null);
    try {
      await fn();
      toast(done);
      await reload();
    } catch (e) {
      setErr((e as ApiError).message);
    }
  };

  return (
    <div style={{ maxWidth: 760, margin: "0 auto" }} className="stack-lg">
      <PageHeader title={t("tools.title")} back="/sell" />

      <div className="card stack">
        <div className="row">
          {l.coverUrl ? <img src={l.coverUrl} alt="" className="tools-cover" /> : null}
          <div className="grow">
            <div style={{ fontWeight: 650 }} dir="auto">{l.title}</div>
            <div className="small muted row wrap" style={{ gap: 6 }}>
              <span className="tag">{t(`sell.status.${l.status}` as MessageKey)}</span>
              {l.priceCents != null && <span className="num">{money(l.priceCents, cur)}</span>}
              {l.wasCents ? <s className="num">{money(l.wasCents, cur)}</s> : null}
              <bdi className="num">{l.quantityAvailable}/{l.quantityTotal}</bdi>
              <VerdictTag v={data.market.verdict} size="sm" />
            </div>
            {l.expiresAt && l.status === "active" && <div className="tiny muted">{t("tools.expiresOn", { d: date(l.expiresAt) })}</div>}
          </div>
          {live && <Link className="btn sm" to={`/l/${l.id}`}>{t("preview.view")}</Link>}
        </div>
        <div className="row wrap">
          {data.bump.canBump ? (
            <AsyncButton className="btn sm primary" onClick={() => run(() => api(`/api/listings/${l.id}/bump`, { body: {} }), t("tools.bumped"))}><IconRocket size={16} /> {t("tools.bump")}</AsyncButton>
          ) : l.status === "active" && data.bump.availableAt ? (
            <span className="tag">{t("tools.bumpAt", { d: relative(data.bump.availableAt) })}</span>
          ) : null}
          {(l.status === "active" || l.status === "sold_out") && (
            <AsyncButton className="btn sm" onClick={() => run(() => api(`/api/listings/${l.id}/pause`, { body: {} }), t("tools.paused"))}>{t("manage.pause")}</AsyncButton>
          )}
          {(l.status === "paused" || l.status === "expired") && (
            <AsyncButton className="btn sm primary" onClick={() => run(() => api(`/api/listings/${l.id}/resume`, { body: {} }), t("tools.renewed"))}>{t(l.status === "expired" ? "tools.renew" : "manage.resume")}</AsyncButton>
          )}
          {l.quantityAvailable > 0 && <button className="btn sm" onClick={() => setSoldOpen(true)}>{t("tools.soldElsewhere")}</button>}
          <Link className="btn sm ghost" to={`/sell/${l.id}?step=details`}>{t("manage.editDetails")}</Link>
        </div>
        {err && <Notice kind="bad">{err}</Notice>}
      </div>

      {data.tips.length > 0 && (
        <section className="tips" aria-labelledby="tips-title">
          <h2 id="tips-title" className="section-title">{t("tools.tips")}</h2>
          <ul>
            {data.tips.map((tip) => (
              <li key={tip.code}>
                <span>{t(`tip.${tip.code}` as MessageKey, { p: tip.value != null ? money(tip.value, cur) : "", n: tip.value ?? "" })}</span>
                {tip.code === "enable_whatsapp" && <Link className="link small" to="/account?tab=contact">{t("tip.enable_whatsapp.cta")}</Link>}
                {(tip.code === "more_photos" || tip.code === "describe") && <Link className="link small" to={`/sell/${l.id}?step=${tip.code === "more_photos" ? "photos" : "details"}`}>{t("tip.fix")}</Link>}
              </li>
            ))}
          </ul>
        </section>
      )}

      <section aria-labelledby="perf-title">
        <h2 id="perf-title" className="section-title">{t("tools.performance")}</h2>
        <div className="kpis">
          <div className="kpi"><span className="small muted"><IconEye size={14} /> {t("tools.views")}</span><b>{data.totals.totalViews}</b><span className="tiny muted">{t("tools.views7d", { n: data.totals.views7d })}</span></div>
          <div className="kpi"><span className="small muted"><IconHeart size={14} /> {t("tools.saves")}</span><b>{data.totals.saves}</b></div>
          <div className="kpi"><span className="small muted"><IconChat size={14} /> {t("tools.chats")}</span><b>{data.totals.chats}</b></div>
          <div className="kpi"><span className="small muted"><IconWhatsApp size={14} /> / <IconCall size={14} /> {t("tools.direct")}</span><b>{data.totals.whatsapp + data.totals.calls}</b><span className="tiny muted"><IconShare size={12} /> {t("tools.shares", { n: data.totals.shares })}</span></div>
        </div>
        <div className="spark" role="img" aria-label={t("tools.chartLabel")}>
          {data.series.map((s) => (
            <span key={s.day} title={`${date(s.day, { day: "numeric", month: "short" })}: ${s.views} / ${s.leads}`}>
              <i style={{ height: `${(s.views / maxBar) * 100}%` }} />
              {s.leads > 0 && <b style={{ height: `${Math.min(100, (s.leads / maxBar) * 100 + 6)}%` }} />}
            </span>
          ))}
        </div>
        <div className="row between tiny muted"><span>{t("tools.days14")}</span><span><i className="key views" /> {t("tools.views")} <i className="key leads" /> {t("tools.leadsKey")}</span></div>
      </section>

      <section aria-labelledby="market-title">
        <h2 id="market-title" className="section-title">{t("tools.market")}</h2>
        {data.market.guide ? (
          <PriceGuideBar guide={data.market.guide} priceCents={l.priceCents} currency={cur} />
        ) : (
          <p className="muted">{t("tools.noMarket")}</p>
        )}
        {data.market.rank && data.market.rank.of > 1 && <p className="small" style={{ marginTop: "var(--s2)" }}>{t("tools.rank", { n: data.market.rank.position, of: data.market.rank.of })}</p>}
        {live && l.priceCents != null && (
          <div className="card stack">
            <div>
              <strong>{t("tools.changePrice")}</strong>
              <div className="tiny muted">{t("tools.dropHint")}</div>
            </div>
            <div className="row">
              <div className="grow"><MoneyInput value={price ?? l.priceCents} onChange={setPrice} currency={cur} /></div>
              <AsyncButton
                className="btn primary"
                disabled={!price || price === l.priceCents}
                onClick={() => run(() => api(`/api/listings/${l.id}`, { method: "PATCH", body: { priceCents: price } }), t("tools.priceSaved"))}
              >
                {t("common.save")}
              </AsyncButton>
            </div>
            <div className="toggle-row" style={{ padding: 0 }}>
              <span>{t("listing.negotiable")}</span>
              <Switch checked={l.negotiable} label={t("listing.negotiable")} onChange={(v) => run(() => api(`/api/listings/${l.id}`, { method: "PATCH", body: { negotiable: v } }), t("tools.saved"))} />
            </div>
          </div>
        )}
      </section>

      <section aria-labelledby="deals-title">
        <h2 id="deals-title" className="section-title">{t("tools.deals", { n: data.deals.length })}</h2>
        {!data.deals.length ? (
          <p className="muted">{t("tools.noDeals")}</p>
        ) : (
          <div className="list">
            {data.deals.map((d) => (
              <Link key={d.id} className={`list-item deal-item${d.awaiting === "you" ? " needs" : ""}`} to={`/inbox/${d.id}`}>
                <div className="grow">
                  <div className="row between"><span className="t" dir="auto">{d.buyerName}</span><span className="tiny muted">{relative(d.lastMessageAt)}</span></div>
                  <div className="row wrap" style={{ gap: 6 }}>
                    <span className="tag">{t(`deal.status.${d.status}` as MessageKey)}</span>
                    {d.agreedPriceCents && <span className="small num">{money(d.agreedPriceCents * d.quantity, cur)}</span>}
                    {d.source !== "chat" && <span className="tag">{t(`deal.source.${d.source}` as MessageKey)}</span>}
                    {d.awaiting === "you" && <span className="tag needs">{t("needs.confirm_handover")}</span>}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      <ConfirmDialog
        open={soldOpen}
        title={t("tools.soldElsewhere")}
        confirmLabel={t("tools.markSold")}
        onClose={() => setSoldOpen(false)}
        onConfirm={() => run(() => api(`/api/listings/${l.id}/sold-elsewhere`, { body: { quantity: soldQty } }), t("tools.soldDone"))}
        body={
          <div className="stack">
            <p className="muted" style={{ margin: 0 }}>{t("tools.soldElsewhereBody")}</p>
            {l.quantityAvailable > 1 && <QtyStepper value={soldQty} max={l.quantityAvailable} onChange={setSoldQty} label={t("common.qty")} />}
          </div>
        }
      />
    </div>
  );
}
